import React, { useState, useMemo, memo } from "react";
import { Drawer, Space, Tooltip, Typography, Select, Button } from "antd";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { FaFilter, FaSort } from "react-icons/fa";
import AppTag from "@lib/components/AppComponents/AppTag";
import { useIntl } from "react-intl";
import { randomColor } from "@lib/helpers/ColorHelper";
import { shortenString } from "@lib/helpers/StringHelper";
import StringOrNumberSearchFilter from "@lib/components/MiniComponents/TableFilters/StringOrNumberSearchFilter";
import DropdownSearchFilter from "@lib/components/MiniComponents/TableFilters/DropdownSearchFilter";
import DateRangeSearchFilter from "@lib/components/MiniComponents/TableFilters/DateRangeSearchFilter";
import {
  StyledMobileTableFiltersContainer,
  StyledMobileTableFiltersRow,
  StyledMobileTableFiltersTagsContainer,
} from "./index.styled";
import { ColumnType } from "antd/lib/table";
import { SorterResult } from "antd/lib/table/interface";
import { type AppTableContentHeaderPropsTypes } from "./AppTableContentHeader";
import { parseGetParamsFromUrl } from "@lib/helpers/UrlHelper";

const AppMobileTableFilters: React.FC<AppTableContentHeaderPropsTypes> = (
  props,
) => {
  const urlParams = parseGetParamsFromUrl(
    props?.query?.data?.config?.url || "",
  );
  const currentSort = {
    field: urlParams?.sort?.split(",")[0],
    order: urlParams?.sort?.split(",")[1] as "ASC" | "DESC",
  };

  const { filters = [], handleRemoveFilters } = props;
  const { columns, onChange } = props.tableProps || {};
  const { messages } = useIntl();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sortDrawerOpen, setSortDrawerOpen] = useState(false);

  // Filterable columns - columns that have filterDropdown
  const filterableColumns = useMemo(() => {
    return (columns || []).filter((col: any) => {
      return col.filterDropdown || col.filters || col.filterType;
    });
  }, [columns]);

  // Sortable columns - columns that have sorter
  const sortableColumns = useMemo(() => {
    return (columns || []).filter((col: any) => {
      return col.sorter && col.dataIndex !== "actions";
    });
  }, [columns]);

  const handleRemoveFilter = (key: string) => {
    if (handleRemoveFilters) {
      handleRemoveFilters([key]);
    }
  };

  const handleSortChange = (params: {
    field: string;
    order: "ASC" | "DESC";
  }) => {
    if (!params?.field || !params?.order) return;

    const sorter: SorterResult<any> = {
      field: params.field,
      order: params.order === "ASC" ? "ascend" : "descend",
    };

    onChange?.({}, {}, sorter, null);
    setSortDrawerOpen(false);
  };

  // Initialize sort fields when drawer opens
  const handleSortDrawerOpen = () => {
    setSortDrawerOpen(true);
  };

  const handleOnChange = (event: {
    field: string;
    value: string | string[];
    label?: string | React.ReactNode;
  }) => {
    const { field, value } = event;
    if (!value) {
      handleRemoveFilter(field);
      setDrawerOpen(false);
      return;
    }
    const tableFilters: Record<string, any> = {
      [field]: value,
    };
    onChange?.({}, tableFilters, {}, null);
    setDrawerOpen(false);
  };

  // Get current filter value for a column
  const getFilterValue = (column: any) => {
    const key = column.key || column.dataIndex;
    const activeFilter = filters.find((f) => f.key === key);

    if (activeFilter) {
      if (activeFilter.operator === "$between") {
        return activeFilter.value.split(",");
      } else if (activeFilter.operator === "$in") {
        return activeFilter.value.split(",");
      } else if (activeFilter.operator === "$cont") {
        // For contains operator, remove % signs
        return activeFilter.value.replace(/%/g, "");
      } else {
        return activeFilter.value;
      }
    }

    return "";
  };

  // Determine filter type from column
  const getFilterType = (
    column: any,
  ): "search" | "dropdown" | "dateRange" | null => {
    if (column.filterType) {
      return column.filterType;
    }

    // Try to infer from filterDropdown
    if (column.filterDropdown) {
      // Check if it's a date field
      const dateFields = ["createdAt", "updatedAt", "dueDate", "confirmedDate"];
      if (dateFields.includes(column.dataIndex as string)) {
        return "dateRange";
      }

      // Check if it has filters array (dropdown)
      if (column.filters && Array.isArray(column.filters)) {
        return "dropdown";
      }

      // Default to search
      return "search";
    }

    // Check if it has filters array
    if (column.filters && Array.isArray(column.filters)) {
      return "dropdown";
    }

    return null;
  };

  if (filterableColumns.length === 0 && sortableColumns.length === 0) {
    return null;
  }

  return (
    <>
      <StyledMobileTableFiltersContainer>
        <StyledMobileTableFiltersRow>
          <StyledMobileTableFiltersTagsContainer>
            {filters && filters.length > 0
              ? filters.map((filter, index) => {
                  const column = columns?.find(
                    (col: any) => (col.key || col.dataIndex) === filter.key,
                  );
                  const title = column?.title
                    ? typeof column.title === "string"
                      ? column.title
                      : (messages[column.title as string] as string) ||
                        filter.title
                    : filter.title;

                  return (
                    <AppTag
                      key={filter.key}
                      closable
                      onClose={() => handleRemoveFilter(filter.key)}
                      color={randomColor(index)}
                    >
                      {title}:{" "}
                      <Tooltip title={filter.value}>
                        <small>{shortenString(filter.value, 15)}</small>
                      </Tooltip>
                    </AppTag>
                  );
                })
              : null}
            {(!filters || filters.length === 0) && (
              <Typography>{messages["common.noFilters"] as string}</Typography>
            )}
          </StyledMobileTableFiltersTagsContainer>
          <Space>
            {filterableColumns.length > 0 && (
              <ActionButton
                type="other"
                icon={<FaFilter />}
                noLabel
                noBg
                size="small"
                onClick={() => setDrawerOpen(true)}
              />
            )}
            {sortableColumns.length > 0 && (
              <ActionButton
                type="other"
                icon={<FaSort />}
                noLabel
                noBg
                size="small"
                onClick={handleSortDrawerOpen}
              />
            )}
          </Space>
        </StyledMobileTableFiltersRow>
      </StyledMobileTableFiltersContainer>

      <Drawer
        title={messages["common.filters"] as string}
        placement="bottom"
        height="80vh"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        styles={{
          body: {
            padding: "16px",
          },
        }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {filterableColumns.map((column: ColumnType<any>) => {
            const key = column?.key || column?.dataIndex;
            const filterType = getFilterType(column);
            const currentValue = getFilterValue(column);
            const title = column?.title as string;

            if (!filterType) return null;

            return (
              <div key={key}>
                {title}
                {filterType === "search" && (
                  <StringOrNumberSearchFilter
                    field={key}
                    label={title}
                    value={
                      !!currentValue && typeof currentValue === "string"
                        ? (() => {
                            try {
                              return decodeURIComponent(currentValue);
                            } catch {
                              return currentValue;
                            }
                          })()
                        : ""
                    }
                    onFinish={handleOnChange}
                  />
                )}
                {filterType === "dropdown" && (
                  <DropdownSearchFilter
                    field={key}
                    data={
                      column?.filters?.map((filter: any) => ({
                        text: filter.text as string,
                        value: filter.value as string,
                      })) || []
                    }
                    value={
                      currentValue
                        ? Array.isArray(currentValue)
                          ? currentValue
                          : [currentValue]
                        : undefined
                    }
                    onFinish={handleOnChange}
                  />
                )}
                {filterType === "dateRange" && (
                  <DateRangeSearchFilter
                    field={key}
                    value={
                      currentValue
                        ? Array.isArray(currentValue)
                          ? currentValue
                          : currentValue.split(",")
                        : undefined
                    }
                    onFinish={handleOnChange}
                  />
                )}
              </div>
            );
          })}
        </Space>
      </Drawer>

      <Drawer
        title={messages["common.sort"] as string}
        placement="bottom"
        height="50vh"
        open={sortDrawerOpen}
        onClose={() => setSortDrawerOpen(false)}
        styles={{
          body: {
            padding: "16px",
          },
        }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div>
            <Typography style={{ marginBottom: "8px" }}>
              {messages["common.sortBy"] as string}:
            </Typography>
            <Select
              style={{ width: "100%" }}
              placeholder={messages["common.selectField"] as string}
              value={currentSort?.field}
              onChange={(value) =>
                handleSortChange({
                  field: value,
                  order: currentSort?.order,
                })
              }
              options={sortableColumns.map((col: any) => {
                const key = col.key || col.dataIndex;
                const title = col.title
                  ? typeof col.title === "string"
                    ? col.title
                    : (messages[`common.${col.title}` as string] as string) ||
                      key
                  : key;
                return {
                  label: title,
                  value: key,
                };
              })}
            />
          </div>
          <div>
            <Typography style={{ marginBottom: "8px" }}>
              {messages["common.sortOrder"] as string}:
            </Typography>
            <Select
              style={{ width: "100%" }}
              value={currentSort?.order}
              onChange={(value) =>
                handleSortChange({
                  field: currentSort?.field,
                  order: value as "ASC" | "DESC",
                })
              }
              options={[
                {
                  label:
                    (messages["common.ascending"] as string) || "Ascending",
                  value: "ASC",
                },
                {
                  label:
                    (messages["common.descending"] as string) || "Descending",
                  value: "DESC",
                },
              ]}
            />
          </div>
          <Button
            type="primary"
            block
            onClick={() =>
              handleSortChange({
                field: currentSort?.field,
                order: currentSort?.order,
              })
            }
            disabled={!currentSort?.field || !currentSort?.order}
          >
            {messages["common.apply"] as string}
          </Button>
        </Space>
      </Drawer>
    </>
  );
};

export default memo(AppMobileTableFilters);
